import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus } from 'lucide-react';
import { Skill, Localized } from '../types';
import { localized } from '../utils';
import { BACKEND_URL } from '../config';

interface Role {
  id: number;
  name: Localized;
}

const CATEGORIES: Array<{ fr: string; en: string }> = [
  { fr: 'Compétence', en: 'Skill' },
  { fr: 'Soft skill', en: 'Soft skill' },
  { fr: 'Langage', en: 'Language' },
  { fr: 'Technologie', en: 'Technology' },
];

export default function ManageSkillsRoles() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const token = localStorage.getItem('token');

  const [skills, setSkills] = useState<(Skill & { id: number })[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);

  const [skillNameFr, setSkillNameFr] = useState('');
  const [skillNameEn, setSkillNameEn] = useState('');
  const [skillCategory, setSkillCategory] = useState(0);

  const [roleNameFr, setRoleNameFr] = useState('');
  const [roleNameEn, setRoleNameEn] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/skills`).then(r => r.json()).then(setSkills).catch(console.error);
    fetch(`${BACKEND_URL}/api/roles`).then(r => r.json()).then(setRoles).catch(console.error);
  }, []);

  const addSkill = async () => {
    if (!skillNameFr || !skillNameEn || !token) return;
    const cat = CATEGORIES[skillCategory];
    const body = { name: { fr: skillNameFr, en: skillNameEn }, category: { fr: cat.fr, en: cat.en } };
    try {
      const res = await fetch(`${BACKEND_URL}/api/skills`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const created = await res.json();
        setSkills([...skills, created]);
        setSkillNameFr('');
        setSkillNameEn('');
      }
    } catch (err) { console.error('Error adding skill:', err); }
  };

  const deleteSkill = async (id: number) => {
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/skills/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setSkills(skills.filter(s => s.id !== id));
    } catch (err) { console.error('Error deleting skill:', err); }
  };

  const addRole = async () => {
    if (!roleNameFr || !roleNameEn || !token) return;
    const body = { name: { fr: roleNameFr, en: roleNameEn } };
    try {
      const res = await fetch(`${BACKEND_URL}/api/roles`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const created = await res.json();
        setRoles([...roles, created]);
        setRoleNameFr('');
        setRoleNameEn('');
      }
    } catch (err) { console.error('Error adding role:', err); }
  };

  const deleteRole = async (id: number) => {
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/roles/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setRoles(roles.filter(r => r.id !== id));
    } catch (err) { console.error('Error deleting role:', err); }
  };

  const grouped: Record<string, (Skill & { id: number })[]> = {};
  skills.forEach(s => {
    const cat = localized(s.category, lang);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(s);
  });

  return (
    <div className="min-h-screen relative z-10 pt-24 pb-8 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-zinc-100 mb-6">{t('manage_skills_roles')}</h1>

        <div className="glass rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-zinc-100 mb-4">{t('skills_skills')}</h2>

          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <select className="glass rounded-lg border border-ink/[0.08] text-zinc-200 px-3 py-2 text-sm" value={skillCategory} onChange={e => setSkillCategory(+e.target.value)}>
              {CATEGORIES.map((cat, i) => (
                <option key={i} value={i}>{lang === 'fr' ? cat.fr : cat.en}</option>
              ))}
            </select>
            <input className="glass rounded-lg flex-1 border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3 py-2 text-sm" placeholder={t('name_fr') as string} value={skillNameFr} onChange={e => setSkillNameFr(e.target.value)} />
            <input className="glass rounded-lg flex-1 border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3 py-2 text-sm" placeholder={t('name_en') as string} value={skillNameEn} onChange={e => setSkillNameEn(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }} />
            <button type="button" onClick={addSkill} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-sm flex items-center gap-1">
              <Plus size={16} /> {t('add_skill')}
            </button>
          </div>

          {skills.length === 0 ? (
            <p className="text-sm text-zinc-500">{t('no_skills')}</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(grouped).map(([category, items]) => (
                <div key={category}>
                  <h3 className="text-sm font-medium text-zinc-500 mb-2">{category}</h3>
                  <div className="flex flex-wrap gap-2">
                    {items.sort((a, b) => localized(a.name, lang).localeCompare(localized(b.name, lang))).map(skill => (
                      <span key={skill.id} className="flex items-center gap-1.5 bg-zinc-800/50 border border-ink/[0.08] px-3 py-1.5 rounded-lg text-sm">
                        <span className="text-zinc-300">{localized(skill.name, lang)}</span>
                        <span className="text-zinc-500 text-xs">({skill.name[lang === 'fr' ? 'en' : 'fr'] || ''})</span>
                        <button onClick={() => deleteSkill(skill.id)} className="text-red-400/70 hover:text-red-400 ml-1"><Trash2 size={14} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass rounded-xl p-6">
          <h2 className="text-lg font-semibold text-zinc-100 mb-4">{t('roles')}</h2>

          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <input className="glass rounded-lg flex-1 border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3 py-2 text-sm" placeholder={t('name_fr') as string} value={roleNameFr} onChange={e => setRoleNameFr(e.target.value)} />
            <input className="glass rounded-lg flex-1 border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3 py-2 text-sm" placeholder={t('name_en') as string} value={roleNameEn} onChange={e => setRoleNameEn(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRole(); } }} />
            <button type="button" onClick={addRole} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-sm flex items-center gap-1">
              <Plus size={16} /> {t('add_role')}
            </button>
          </div>

          {roles.length === 0 ? (
            <p className="text-sm text-zinc-500">{t('no_roles')}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {roles.map(role => (
                <span key={role.id} className="flex items-center gap-1.5 bg-zinc-800/50 border border-ink/[0.08] px-3 py-1.5 rounded-lg text-sm">
                  <span className="text-zinc-300">{localized(role.name, lang)}</span>
                  <span className="text-zinc-500 text-xs">({role.name[lang === 'fr' ? 'en' : 'fr'] || ''})</span>
                  <button onClick={() => deleteRole(role.id)} className="text-red-400/70 hover:text-red-400 ml-1"><Trash2 size={14} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
